/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { withI18n } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useHistory, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { setAuthToken, setJWT, setRefreshToken } from '@/redux/actions/auth';
import { loadSummary } from '@/redux/actions/summary';
import Notifier from '@/components/Notification/Notifier/index';
import { loadOnboardingDomains } from '@/redux/actions/onboarding';
import { loadFeatureToggles } from '@/redux/actions/featureToggle';
import { loadCountryCodes } from '@/redux/actions/countryPhoneCodes';
import { setCookie, getCookie, deleteCookie } from '@/utils/Application/cookies';
import { loadScript } from '@/utils/ThirdParties/loadUniqueScripts';
import { initWootric } from '@/utils/ThirdParties/wootric';
import { initDataDog } from '@/utils/ThirdParties/datadog';
import { loadGoogleTagManagerScripts, onLoadHotjar } from '@/utils/ThirdParties/tagManager';
import { notifierError } from '@/utils/Application/notifier';
import {
  config, IS_PROD, COUNTRY, AMPLITUDE_KEY,
} from '@/config';
import { WOOTRIC_URL } from '@/config/externalScriptsUrl';
import { LOGOUT_URL } from '@/config/whmcsUrls';
import { enqueueSnackbar } from '@/redux/actions/notifications';
import { loadUnpaidInvoices } from '@/redux/actions/unpaidInvoices';
import { loadAlerts } from '@/redux/actions/alerts';
import { getLastSharedBoarding, skipSurveyLocalStorage } from '@/pages/CustomerProfileSurvey/utils';
import { skipCustomerSurvey, verifyResponseCustomerProfileSurvey } from '@/redux/actions/customerProfileSurvey';
import AppVersion from '@/components/Labels/AppVersion/AppVersion';
import { queryParams } from '@/utils/Application/queryParams';
import { ChatButton } from '@/components/Buttons/ChatButton';
import { Notifications } from '@/pages/common/';
import Main from './Main';
import SsoAcceptPage from '@/pages/SSO/SsoAcceptPage';
import {
  init as initAmplitude, Identify as IdentifyObject, identify as identifyAmplitude,
} from '@amplitude/analytics-browser';
import { format } from 'date-fns';
import { Growthbook } from '@/config/GrowthBook';
import { VideoClasses } from '@/components/VideoClasses';
import LoaderPage from './Loader/LoaderPage';
import { PromotionBanner } from '@/components/PromotionBanner';
import { GrowthbookTestToggle } from '@/components/GrowthbookTestToggle';
import {
  commonActions, merlinActions, agentsFactoryActions,
} from '@/redux/modules';
import { loadDomains } from '@/redux/actions/domains';
import { loadGeneralProducts } from '@/redux/actions/generalProducts';
import { useChatbot } from '@/contexts/Chatbot';
import logger from '@/utils/logger';
import { ChatbotLogEvents } from '@/contexts/Chatbot/Chatbot.types';
import { useAiChat } from '@/hooks/AiChat';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  FOXTROT_NEW_AI_CHAT_INTERFACE,
  AI_AGENTS_SITE_BUILDER_AGENT_FACTORY,
  OPERACOES_ENABLE_AI_CHAT_MULTI_AGENTS,
  TEST_TOGGLE_GB_TIMING,
} from '@/config/GrowthBook/constants';


const Pages = ({ t }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { jwt, token } = useSelector(state => state.auth);
  const isAuthenticated = useSelector(state => !!state.auth.token);
  const isSsoAccept = window.location.pathname === '/sso-accept';
  const summary = useSelector(state => state.summary);
  const websiteBuilderDomains = useSelector(state => state.onboarding);
  const featureToggles = useSelector(state => state.featureToggles.toggles);
  const { listTotal: totalDomains, loaded: domainsLoaded } = useSelector(state => state.newDomains);
  const { bkpProducts: totalHostings, loadedDate: loadedDateGeneralProducts } = useSelector(state => state.generalProducts);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [executedHotjar, setExecutedHotjar] = useState(false);
  const [gbReady, setGbReady] = useState(false);
  const gbLoadPromiseRef = useRef(null);
  const prevMultiAgentRef = useRef(null);
  const { triggerExternalMessage } = useAiChat();
  const { messageFromRequest } = useSelector(state => state.merlin);
  const newAiChatInterface = useFeatureIsOn(FOXTROT_NEW_AI_CHAT_INTERFACE);
  const agentFactoryFeature = useFeatureIsOn(AI_AGENTS_SITE_BUILDER_AGENT_FACTORY);
  const enableMultiAgent = useFeatureIsOn(OPERACOES_ENABLE_AI_CHAT_MULTI_AGENTS);
  const isTestToggleActive = useFeatureIsOn(TEST_TOGGLE_GB_TIMING);
  const chatbot = useChatbot();

  const {
    loadingVerify: loadingVerifyCustomerSurvey,
    hasResponse: hasResponseCustomerSurvey,
  } = useSelector(state => state.customerProfileSurvey);

  const shouldShowMain = isAuthenticated && summary.id && !loadingVerifyCustomerSurvey && gbReady;

  const triggerMessageFromRequest = useCallback(() => {
    if (!shouldShowMain || !messageFromRequest || !Growthbook.ready) return;

    const chatbotIsReady = chatbot.isActive && chatbot.isReady;
    if (newAiChatInterface && !chatbotIsReady) return;

    triggerExternalMessage({ message: messageFromRequest, fullScreen: true });
    dispatch(merlinActions.inhouse.setMessageFromRequest(''));
  }, [
    messageFromRequest,
    dispatch,
    newAiChatInterface,
    chatbot.isActive,
    chatbot.isReady,
    shouldShowMain,
    Growthbook.ready,
  ]);

  useEffect(() => {
    if (Growthbook.ready) {
      initDataDog();
    }
  }, [Growthbook.ready]);

  useEffect(() => {
    if (chatbot.isActive && summary.id) logger.info(ChatbotLogEvents.AI_CHAT_ACTIVATED, { clientId: summary.id });
    if (!summary.id || !jwt || !chatbot.isActive || !gbReady) return;
    logger.info(ChatbotLogEvents.AI_CHAT_INIT, {
      id: summary.id, jwt, isActive: chatbot.isActive, clientId: summary.id,
    });
    chatbot.init({
      fullScreen: {
        parentElementId: 'ai-chat-full-screen-container',
      },
      user: {
        id: summary.id,
        firstname: summary.firstname,
        email: summary.email,
      },
      auth: {
        jwt,
        token,
      },
      host: {
        name: 'clientarea',
      },
      websocketType: enableMultiAgent ? 'multi-agent' : 'n8n',
      agentType: 'gator',
    });
  }, [summary, jwt, chatbot.isActive, enableMultiAgent, gbReady]);

  useEffect(() => {
    if (!chatbot.isActive || !gbReady) return;
    if (prevMultiAgentRef.current === null) {
      prevMultiAgentRef.current = enableMultiAgent;
      return;
    }
    if (prevMultiAgentRef.current !== enableMultiAgent) {
      prevMultiAgentRef.current = enableMultiAgent;
      chatbot.setProps({
        websocketType: enableMultiAgent ? 'multi-agent' : 'n8n',
        agentType: 'gator',
      });
    }
  }, [enableMultiAgent, chatbot.isActive, gbReady, chatbot.setProps]);

  const location = useLocation();

  const handleBoletoProcessError = () => {
    const url = new URL(window.location);
    const searchParams = new URLSearchParams(url.search);
    if (searchParams.has('process_error')) {
      const error = searchParams.get('process_error');
      if (error === 'boleto') {
        dispatch(enqueueSnackbar('snackbar.error.process_boleto', notifierError));
      }
    }
  };

  useEffect(() => {
    if (isSsoAccept) return;

    const { COUNTRY } = config;
    COUNTRY === 'br' && handleBoletoProcessError();
    const token = getCookie('hg-token');
    if (token) {
      localStorage.setItem('token', token);
    }
    const tokenLocal = localStorage.getItem('token');
    const prompt = queryParams(window.location.search, 'prompt');
    let hgPrompt = localStorage.getItem('hg_prompt');


    const redirectPath = getCookie('hg_car');
    if (redirectPath) {
      setCookie('hg_car', '', false, -1);
    }

    if (!tokenLocal) {
      const tenMinutes = 10 * 60 * 1000;
      const accessingUrl = JSON.stringify({
        value: window.location.pathname,
        expiry: Date.now() + tenMinutes,
      });
      localStorage.setItem('accessing_url', accessingUrl);
      if (prompt) {
        const fiveMinuts = 5 * 60 * 1000;
        const item = {
          value: prompt,
          expiry: Date.now() + fiveMinuts,
        };
        localStorage.setItem('hg_prompt', JSON.stringify(item));
      }

      setCookie('hg_car', btoa(`${location.pathname}${location.search}`), false, 1);
      window.location = LOGOUT_URL;
    }

    if (hgPrompt) {
      const item = JSON.parse(hgPrompt);
      if (Date.now() > item.expiry) {
        localStorage.removeItem('hg_prompt');
        hgPrompt = null;
      }
    }

    if (tokenLocal && (prompt || hgPrompt)) {
      const promptId = prompt || (hgPrompt && JSON.parse(hgPrompt).value);
      dispatch(commonActions.aiChatGetPrompts({ promptId }));
      localStorage.removeItem('hg_prompt');
    }

    document.title = t('appTitle');
    loadGoogleTagManagerScripts();

    deleteCookie('merlinUrl');
    dispatch(setAuthToken(localStorage.getItem('token')));
    dispatch(setJWT(Cookies.get('hg-service-token')));
    dispatch(setRefreshToken(Cookies.get('hg-refresh-token')));
    dispatch(loadFeatureToggles());
    dispatch(loadSummary());
    dispatch(loadUnpaidInvoices());
    dispatch(loadAlerts());
    dispatch(loadCountryCodes());
    dispatch(loadDomains({}));
    dispatch(loadGeneralProducts());
    websiteBuilderDomains.products.length === 0 && dispatch(loadOnboardingDomains());
    gbLoadPromiseRef.current = Growthbook.loadFeatures({ autoRefresh: true });
    dispatch(commonActions.serverTime.request());

    const accessingUrl = localStorage.getItem('accessing_url');
    if (accessingUrl) {
      const accessingUrlObj = JSON.parse(accessingUrl);
      if (Date.now() < accessingUrlObj.expiry) {
        history.replace(accessingUrlObj.value);
      }
      localStorage.removeItem('accessing_url');
    }
  }, []);

  useEffect(() => {
    if (!summary || !summary.id) return;

    const cookieValue = getCookie('agent-factory-auth');
    if (cookieValue) {
      try {
        const parsed = JSON.parse(cookieValue);
        if (!parsed.client_id || parsed.client_id !== summary.id) {
          deleteCookie('agent-factory-auth');
        }
      } catch (e) {
        deleteCookie('agent-factory-auth');
      }
    }

    if (summary.features && summary.features.agent_factory && agentFactoryFeature) {
      dispatch(agentsFactoryActions.signup.request());
    }
  }, [agentFactoryFeature, dispatch, summary]);

  useEffect(() => {
    triggerMessageFromRequest();
  }, [triggerMessageFromRequest]);

  useEffect(() => {
    let GrowthbookCancelled = false;
    if (summary.id) {
      const amplitudeUser = `${summary.country}_${summary.id}`;
      const amplitudeConfigurationObj = {
        pageViewTracking: true,
      };

      if (IS_PROD) {
        initAmplitude(AMPLITUDE_KEY, amplitudeUser, amplitudeConfigurationObj);
        const identifyObject = new IdentifyObject();
        identifyObject.set('init_session', location.pathname);
        identifyObject.set('country_whmcs', COUNTRY);
        identifyObject.set('sign_up', summary.datecreated);
        identifyAmplitude(identifyObject);
      }

      Growthbook.setAttributes({
        id: String(summary.id),
        brand: COUNTRY,
      });

      const loadPromise = gbLoadPromiseRef.current
        || Growthbook.loadFeatures({ autoRefresh: true });
      gbLoadPromiseRef.current = loadPromise;

      loadPromise
        .catch((error) => { logger.error('Growthbook - Error loading Growthbook features', error); })
        .finally(() => {
          if (!GrowthbookCancelled) setGbReady(true);
        });
    }
    return () => { GrowthbookCancelled = true; };
  }, [(summary && summary.id)]);

  useEffect(() => {
    if (featureToggles && summary.id && summary.properties.persona !== '') {
      const { wootric } = featureToggles;
      const { properties } = summary;
      if (properties && wootric) {
        if (properties.active || properties.showWootric) {
          loadScript({ id: 'wootric', src: WOOTRIC_URL }).then(() => {
            initWootric(properties);
          });
        }
      }
    }
  }, [featureToggles, summary]);

  useEffect(() => {
    if (summary.id && domainsLoaded && loadedDateGeneralProducts && !executedHotjar) {
      onLoadHotjar({
        id: `${COUNTRY}_${summary.id}`,
        signUp: summary.datecreated,
        lastLogin: format(new Date(), 'yyyy-MM-dd'),
        user_id: summary.id,
        hosting_product: totalHostings.length > 0,
        domain_product: totalDomains > 0,
      });

      setExecutedHotjar(true);
    }
  }, [summary.id, domainsLoaded, loadedDateGeneralProducts, executedHotjar]);

  useEffect(() => {
    const skip = queryParams(window.location.search, 'skip');
    const skipForAdmin = skipSurveyLocalStorage.get();
    if (skip && !skipForAdmin) { skipSurveyLocalStorage.set(); }
    if (skip === 'false' && skipForAdmin) { skipSurveyLocalStorage.remove(); }
  }, []);

  useEffect(() => {
    if (websiteBuilderDomains.products && summary.id) {
      const { hasSharedBoarding, onboardingFilter } = getLastSharedBoarding(websiteBuilderDomains.products);

      if (hasSharedBoarding && !hasResponseCustomerSurvey) {
        const { hostingId } = onboardingFilter;

        dispatch(verifyResponseCustomerProfileSurvey(summary.id, hostingId)).then(() => {
          dispatch(skipCustomerSurvey(false));
        });
      }

      if (!hasSharedBoarding) {
        dispatch(skipCustomerSurvey(true));
      }
    }
  }, [websiteBuilderDomains.products, summary.id]);

  const updateLoadingProgress = useCallback(() => {
    if (isAuthenticated && loadingProgress === 0) {
      setLoadingProgress(33);
    }

    if (summary.id && loadingProgress === 33) {
      setLoadingProgress(66);
    }

    if (!loadingVerifyCustomerSurvey && gbReady && loadingProgress === 66) {
      setLoadingProgress(100);
    }
  }, [isAuthenticated, summary.id, loadingVerifyCustomerSurvey, gbReady]);

  useEffect(() => {
    updateLoadingProgress();
  }, [updateLoadingProgress]);


  useEffect(() => {
    if (isTestToggleActive) {
      console.log('index.js (isTestToggleActive) - userId:', { id: summary.id, gbReady });
    }
  }, [isTestToggleActive]);

  useEffect(() => {
    if (summary.id && isTestToggleActive) {
      console.log('index.js (summary.id && isTestToggleActive) - userId:', { id: summary.id, gbReady });
    }
  }, [summary.id, isTestToggleActive]);

  useEffect(() => {
    if (gbReady && isTestToggleActive) {
      console.log('index.js (gbReady && isTestToggleActive) - userId:', summary.id);
    }
  }, [gbReady, isTestToggleActive]);

  if (isSsoAccept) {
    return (
      <div className="App">
        <Route path="/sso-accept" component={SsoAcceptPage} />
      </div>
    );
  }

  return (
    <div className="App">
      <Notifications />
      {shouldShowMain ? (
        <Route path="/" component={Main} />
      ) : (
        <LoaderPage progress={loadingProgress} />
      )}
      {!IS_PROD && <AppVersion />}
      <Notifier />
      <VideoClasses />
      <PromotionBanner />
      <ChatButton />
      <GrowthbookTestToggle />
    </div>
  );
};

export default withI18n()(Pages);
